import {
  interaction,
  SizableText,
  Stack,
  styled,
  XStack,
  YStack,
} from '@mythical/ui'
import { ButtonLink } from 'app/components/primitive/Button'
import RichText from 'app/components/primitive/RichText'
import { SmartLink } from 'app/components/primitive/SmartLink'
import { Container, ContainerProps } from 'app/components/structure/Container'
import React from 'react'
import { SolitoImage } from 'solito/image'

const AdGridWrapper = styled(XStack, {
  fd: 'column',
  gap: '$8.5',
  w: '100%',
  $gtSm: {
    fd: 'row',
    gap: '$3.5',
  },
})

const AdGridItem = styled(YStack, {
  gap: '$3.5',
  w: '100%',
  $gtSm: {
    flex: 1,
  },
})

const AdGridContent = styled(YStack, {
  flex: 1,
  gap: '$3.5',
  jc: 'space-between',
})

export interface AdGridProps extends ContainerProps {
  items: {
    image: string
    title?: string
    description?: string
    richTextDescription?: React.ReactNode
    href?: string
    ctaLabel?: string
  }[]
}

export const AdGrid = ({ items, ...props }: AdGridProps) => {
  return (
    <Container paddedX variant="none" {...props}>
      <AdGridWrapper>
        {items.map((ad) => (
          <AdGridItem key={ad.image}>
            <SmartLink
              Component={Stack}
              href={ad.href}
              aspectRatio={16 / 9}
              br={6}
              overflow="hidden"
              tabIndex={-1}
              w="100%"
              {...(ad.href && interaction.opacity)}
              {...(ad.href?.startsWith('http') ? { target: '_blank' } : {})}
            >
              <SolitoImage
                alt=""
                contentFit="cover"
                fill
                sizes="(min-width: 800px) 50vw, 100vw"
                src={ad.image}
              />
            </SmartLink>

            {(ad.title || ad.description || ad.richTextDescription) && (
              <AdGridContent>
                <YStack gap="$2">
                  {ad.title && (
                    <SizableText ff="$display" tt="uppercase">
                      {ad.title}
                    </SizableText>
                  )}
                  {ad.richTextDescription && (
                    <YStack className="line-clamp-3">
                      <RichText
                        content={ad.richTextDescription}
                        textProps={{
                          size: '$s',
                          fontWeight: '500',
                        }}
                      />
                    </YStack>
                  )}
                </YStack>
                {ad.ctaLabel && ad.href && (
                  <ButtonLink
                    alignSelf="flex-start"
                    href={ad.href}
                    size="$4.5"
                    textProps={{
                      ff: '$title',
                      fontWeight: '500',
                    }}
                    {...(ad.href?.startsWith('http')
                      ? { target: '_blank' }
                      : {})}
                  >
                    {ad.ctaLabel}
                  </ButtonLink>
                )}
              </AdGridContent>
            )}
          </AdGridItem>
        ))}
      </AdGridWrapper>
    </Container>
  )
}
